import { useMemo } from 'react';
import { Group, NumberInput, Stack, TextInput } from '@mantine/core';
import { useIntl } from 'react-intl';

import { handlingUnitsStepMessages } from './handling-units-step-messages';
import { PACKAGING_TYPE_OPTIONS } from '../../../../ltl/constants';
import { Dropdown } from '../../../common/Dropdown';

import type { LtlCommodity } from '../../../../types/ltl';

export type CommodityDetailsProps = {
  commodity: LtlCommodity;
  index: number;
  onChange: (next: LtlCommodity) => void;
  /**
   * Formatted inline error messages keyed by field path, already sliced to this
   * commodity's prefix (e.g. `'pieceType'`, `'numberOfPieces'`).
   */
  fieldErrors?: Record<string, string>;
  /** When false, Piece Type/Piece Quantity are never marked required even if the commodity is hazmat. Defaults to true. */
  hazmatFieldsRequired?: boolean;
};

/**
 * The "Show commodity details" reveal block for a commodity card: NMFC code,
 * piece type, number of pieces, and commodity description. Piece type and
 * number of pieces become required only while the commodity is marked hazmat.
 */
export const CommodityDetails = ({ commodity, index, onChange, fieldErrors, hazmatFieldsRequired = true }: CommodityDetailsProps) => {
  const intl = useIntl();
  const arePieceFieldsRequired = commodity.isHazmat && hazmatFieldsRequired;

  const pieceTypeData = useMemo(
    () => PACKAGING_TYPE_OPTIONS.map((option) => ({ value: option.value, label: intl.formatMessage(option.label) })),
    [intl],
  );

  const handleNumberOfPiecesChange = (value: number | string) => {
    const parsed = typeof value === 'string' ? parseInt(value, 10) : value;
    onChange({ ...commodity, numberOfPieces: Number.isNaN(parsed) ? undefined : parsed });
  };

  return (
    <Stack gap='sm'>
      <Group grow>
        <TextInput
          data-testid={`commodity-nmfc-${index}`}
          label={intl.formatMessage(handlingUnitsStepMessages.nmfcCodeLabel)}
          onChange={(event) => onChange({ ...commodity, nmfcCode: event.currentTarget.value })}
          placeholder={intl.formatMessage(handlingUnitsStepMessages.nmfcCodePlaceholder)}
          value={commodity.nmfcCode}
        />
        <Dropdown
          clearable
          data={pieceTypeData}
          data-testid={`commodity-piece-${index}`}
          error={fieldErrors?.pieceType}
          label={intl.formatMessage(handlingUnitsStepMessages.pieceLabel)}
          onChange={(value) => onChange({ ...commodity, pieceType: value ?? undefined })}
          placeholder={intl.formatMessage(handlingUnitsStepMessages.piecePlaceholder)}
          value={commodity.pieceType ?? null}
          withAsterisk={arePieceFieldsRequired}
        />
        <NumberInput
          allowNegative={false}
          data-testid={`commodity-number-of-pieces-${index}`}
          error={fieldErrors?.numberOfPieces}
          label={intl.formatMessage(handlingUnitsStepMessages.numberOfPiecesLabel)}
          min={0}
          onChange={handleNumberOfPiecesChange}
          value={commodity.numberOfPieces ?? ''}
          withAsterisk={arePieceFieldsRequired}
        />
      </Group>
      <TextInput
        data-testid={`commodity-description-${index}`}
        label={intl.formatMessage(handlingUnitsStepMessages.commodityDescriptionLabel)}
        onChange={(event) => onChange({ ...commodity, description: event.currentTarget.value })}
        placeholder={intl.formatMessage(handlingUnitsStepMessages.commodityDescriptionPlaceholder)}
        value={commodity.description}
      />
    </Stack>
  );
};
